/**
 * Institutions and the services they sell.
 *
 * The system was built around one shape: a doctor, their clinics, and a patient
 * booking a consultation. Hospitals, polyclinics and lab centres do not fit
 * that — a surgery is not a consultation, an ICU bed is not a clinic slot, and
 * the money from a hospital doctor's visit belongs to the hospital rather than
 * to the doctor.
 *
 * Rather than six new booking systems, there is one `InstitutionService` with a
 * category. Surgeries, lab tests, scans, inpatient stays, ICU and physiotherapy
 * differ in what they are called and what they cost, not in how they are sold:
 * every one of them is "a thing with a price, a duration, a schedule and a
 * deposit". Modelling them separately would mean six tables, six routes and six
 * screens that drift apart; modelling them as one means a seventh category is a
 * row, not a migration.
 *
 * This module is the arithmetic and the vocabulary, with no I/O, so the server
 * and the browser can share it and a test can exercise it without a database.
 */

/* ==========================================================================
   Vocabulary
   ========================================================================== */

/** What kind of place this is. Drives which services it may offer. */
export const INSTITUTION_TYPES = {
  HOSPITAL: 'hospital',
  POLYCLINIC: 'polyclinic',
  LAB_CENTER: 'lab_center',
  PHARMACY: 'pharmacy',
};

export const INSTITUTION_TYPE_LABELS = {
  [INSTITUTION_TYPES.HOSPITAL]: 'مستشفى',
  [INSTITUTION_TYPES.POLYCLINIC]: 'مركز طبي',
  [INSTITUTION_TYPES.LAB_CENTER]: 'مركز تحاليل وأشعة',
  [INSTITUTION_TYPES.PHARMACY]: 'صيدلية',
};

/** One row per sellable thing. The discriminator on InstitutionService. */
export const SERVICE_CATEGORIES = {
  SURGERY: 'surgery',
  LAB: 'lab',
  RADIOLOGY: 'radiology',
  INPATIENT: 'inpatient',
  ICU: 'icu',
  PHYSIOTHERAPY: 'physiotherapy',
};

export const SERVICE_CATEGORY_LABELS = {
  [SERVICE_CATEGORIES.SURGERY]: 'عمليات جراحية',
  [SERVICE_CATEGORIES.LAB]: 'تحاليل طبية',
  [SERVICE_CATEGORIES.RADIOLOGY]: 'أشعة وتصوير',
  [SERVICE_CATEGORIES.INPATIENT]: 'حجز داخلي',
  [SERVICE_CATEGORIES.ICU]: 'رعاية مركزة',
  [SERVICE_CATEGORIES.PHYSIOTHERAPY]: 'علاج طبيعي',
};

/**
 * Which categories each institution type may sell.
 *
 * A polyclinic runs consultations through the existing doctor/clinic machinery
 * and sells nothing else; that is the whole difference between it and a
 * hospital. A lab centre sells tests and scans and does not admit anyone.
 *
 * The server enforces this on write. Repeating it here is what lets the
 * institution's own screens hide sections it can never use, rather than
 * offering a form that will be refused.
 */
export const TYPE_CAPABILITIES = {
  [INSTITUTION_TYPES.HOSPITAL]: [
    SERVICE_CATEGORIES.SURGERY,
    SERVICE_CATEGORIES.LAB,
    SERVICE_CATEGORIES.RADIOLOGY,
    SERVICE_CATEGORIES.INPATIENT,
    SERVICE_CATEGORIES.ICU,
    SERVICE_CATEGORIES.PHYSIOTHERAPY,
  ],
  [INSTITUTION_TYPES.POLYCLINIC]: [],
  [INSTITUTION_TYPES.LAB_CENTER]: [SERVICE_CATEGORIES.LAB, SERVICE_CATEGORIES.RADIOLOGY],
  [INSTITUTION_TYPES.PHARMACY]: [],
};

export const capabilitiesFor = (type) => TYPE_CAPABILITIES[type] || [];

/** Whether this institution may sell that category at all. */
export const canOffer = (type, category) => capabilitiesFor(type).includes(category);

/**
 * Every institution runs consultations except a pharmacy.
 *
 * A hospital and a polyclinic both employ doctors; a lab centre may have a
 * consultant reading scans. Only a pharmacy has no consultation side at all.
 */
export const runsConsultations = (type) => type !== INSTITUTION_TYPES.PHARMACY;

/* ==========================================================================
   The patient's tabs
   ========================================================================== */

/**
 * The patient dashboard, top level.
 *
 * Ordered by how often a patient needs each: a consultation is the common case,
 * a surgery is rare and deliberate. The pharmacy tab is declared here with no
 * categories behind it so the navigation is complete and the screen can say
 * "قريباً" honestly, rather than the tab appearing later and moving everything.
 */
export const PATIENT_TABS = [
  {
    id: 'consultations',
    label: 'الكشوفات والمتابعات',
    icon: 'stethoscope',
    accent: '#22d3ee',
    categories: [],
    kind: 'consultations',
  },
  {
    id: 'diagnostics',
    label: 'التحاليل والأشعة',
    icon: 'flask-conical',
    accent: '#34d399',
    categories: [SERVICE_CATEGORIES.LAB, SERVICE_CATEGORIES.RADIOLOGY],
    kind: 'services',
  },
  {
    id: 'surgeries',
    label: 'العمليات الجراحية',
    icon: 'scissors',
    accent: '#f472b6',
    categories: [SERVICE_CATEGORIES.SURGERY],
    kind: 'services',
  },
  {
    id: 'inpatient',
    label: 'الحجز الداخلي والرعاية',
    icon: 'bed',
    accent: '#a78bfa',
    categories: [
      SERVICE_CATEGORIES.INPATIENT,
      SERVICE_CATEGORIES.ICU,
      SERVICE_CATEGORIES.PHYSIOTHERAPY,
    ],
    kind: 'services',
  },
  {
    id: 'pharmacies',
    label: 'الصيدليات',
    icon: 'pill',
    accent: '#fbbf24',
    categories: [],
    kind: 'soon',
  },
];

export const tabById = (id) => PATIENT_TABS.find((tab) => tab.id === id) || null;

/** The categories one tab draws from — the query the catalogue screen runs. */
export const categoriesForTab = (id) => tabById(id)?.categories || [];

/* ==========================================================================
   Money
   ========================================================================== */

const num = (value, fallback = 0) => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampPercent = (value) => Math.min(100, Math.max(0, num(value, 0)));

/**
 * What a service costs this patient.
 *
 * Two discounts stack, and they stack the way every other discount in this
 * system does — multiplicatively, rounded once at the end. A 20% public offer
 * and a 20% insurance rate is 36% off, not 40%: the insurer discounts the
 * already-reduced price, because that is the price being charged.
 *
 * The home-visit fee is added *after* both. It pays for someone's travel, and
 * discounting a nurse's petrol on the lab's behalf is not the offer's to give —
 * the same reason clinic extras sit outside the consultation discount chain.
 *
 * The deposit is a percentage of the service, not of the travel, for the same
 * reason: it holds the appointment.
 *
 * @returns {{
 *   basePrice: number, publicPercent: number, insurancePercent: number,
 *   afterPublic: number, servicePrice: number, homeVisitFee: number,
 *   total: number, saving: number, insuranceSaving: number,
 *   deposit: { required: boolean, percent: number, amount: number }
 * }}
 */
export function priceService({ service, hasInsurance = false, homeVisit = false } = {}) {
  const basePrice = Math.max(0, num(service?.basePrice, 0));

  const publicPercent = clampPercent(service?.discountPercent);
  // Insurance only applies when the patient actually carries it and the service
  // offers a rate. Both halves matter — a service with no insurance rate charges
  // an insured patient the ordinary price.
  const insurancePercent = hasInsurance ? clampPercent(service?.insuranceDiscountPercent) : 0;

  const afterPublicRaw = basePrice * (1 - publicPercent / 100);
  const afterInsuranceRaw = afterPublicRaw * (1 - insurancePercent / 100);

  const servicePrice = Math.round(afterInsuranceRaw);
  const afterPublic = Math.round(afterPublicRaw);

  // Only charged when the visit is actually at home, and only when offered.
  const homeVisitFee =
    homeVisit && service?.homeVisitAvailable ? Math.max(0, Math.round(num(service?.homeVisitFee, 0))) : 0;

  const total = servicePrice + homeVisitFee;

  const depositRequired = service?.depositRequired === true || service?.depositRequired === 'true';
  const depositPercent = depositRequired ? clampPercent(service?.depositPercent) : 0;

  // An explicit amount wins over the percentage, matching the clinic deposit.
  const explicitAmount = service?.depositAmount;
  const hasExplicit = explicitAmount !== null && explicitAmount !== undefined && explicitAmount !== '';

  const depositAmount = depositRequired
    ? Math.round(hasExplicit ? Math.max(0, num(explicitAmount, 0)) : (depositPercent / 100) * servicePrice)
    : 0;

  return {
    basePrice,
    publicPercent,
    insurancePercent,
    afterPublic,
    servicePrice,
    homeVisitFee,
    total,
    saving: Math.max(0, basePrice - servicePrice),
    insuranceSaving: Math.max(0, afterPublic - servicePrice),
    deposit: {
      required: depositRequired,
      percent: depositPercent,
      // A deposit can never exceed what is being held.
      amount: Math.min(depositAmount, servicePrice),
    },
  };
}

/**
 * The cheapest offer for one service across institutions.
 *
 * The patient screen exists so a "صورة دم شاملة" can be compared rather than
 * accepted from whoever appeared first, so the list leads with the real total
 * for this patient — insurance included — not the list price.
 */
export function cheapestOffer(services = [], { hasInsurance = false } = {}) {
  const priced = (Array.isArray(services) ? services : [])
    .filter(Boolean)
    .map((service) => ({ service, quote: priceService({ service, hasInsurance }) }));

  if (priced.length === 0) return null;

  return priced.reduce((best, entry) => (entry.quote.total < best.quote.total ? entry : best));
}

/* ==========================================================================
   Scheduling
   ========================================================================== */

const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

export const dayKeyOf = (dateKey) => {
  const date = new Date(`${dateKey}T00:00:00`);
  return Number.isNaN(date.getTime()) ? '' : DAY_KEYS[date.getDay()];
};

export function toMinutes(time) {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(time || ''));
  if (!match) return NaN;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return NaN;
  return hours * 60 + minutes;
}

export function toTime(minutes) {
  const safe = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(safe / 60)).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`;
}

/**
 * The times a service can start on one day.
 *
 * Deliberately the same shape as a clinic's slot grid — a service carries
 * workingDays, a window and a slot length — because the patient is doing the
 * same thing either way: picking a time off a calendar. Reusing the shape means
 * the booking screen is the one that already exists.
 *
 * An admitted stay (inpatient, ICU) is booked by *day* rather than by time, so
 * a service with no window yields a single all-day slot rather than nothing.
 */
export function serviceSlotsForDate(service, dateKey) {
  if (!service) return [];

  const days = Array.isArray(service.workingDays) ? service.workingDays : [];
  const day = dayKeyOf(dateKey);
  if (!day) return [];
  if (days.length > 0 && !days.some((d) => String(d).toLowerCase() === day)) return [];

  const open = toMinutes(service.startTime);
  const close = toMinutes(service.endTime);
  const step = Number(service.slotMinutes) || 0;

  // Admission by the day: one slot, no clock.
  if (Number.isNaN(open) || Number.isNaN(close) || step <= 0 || close <= open) {
    return service.admissionByDay ? ['00:00'] : [];
  }

  const slots = [];
  for (let start = open; start + step <= close; start += step) {
    slots.push(toTime(start));
  }
  return slots;
}

/** Bookings that still hold their slot. A cancelled one frees it. */
export const LIVE_SERVICE_STATUSES = ['pending', 'confirmed', 'completed'];

export const holdsSlot = (booking) =>
  Boolean(booking) && LIVE_SERVICE_STATUSES.includes(booking.status || 'pending');

/**
 * One day of a service's calendar, with each slot's remaining room.
 *
 * `capacityPerSlot` is what separates these from a clinic: one theatre runs one
 * operation at a time, but a lab can draw twenty samples in the same half hour,
 * and an ICU has as many slots as it has beds.
 */
export function serviceAvailability({ service, bookings = [], dateKey, now = new Date() }) {
  const slots = serviceSlotsForDate(service, dateKey);
  if (slots.length === 0) return [];

  const capacity = Math.max(1, Number(service?.capacityPerSlot) || 1);

  const sameDay = (Array.isArray(bookings) ? bookings : []).filter(
    (booking) =>
      booking &&
      String(booking.date) === String(dateKey) &&
      String(booking.serviceId) === String(service.id) &&
      holdsSlot(booking),
  );

  const todayKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  return slots.map((time) => {
    const taken = sameDay.filter((booking) => booking.time === time).length;
    // A slot earlier today has gone, but an all-day admission has not.
    const isPast =
      dateKey < todayKey ||
      (dateKey === todayKey && !service.admissionByDay && toMinutes(time) <= nowMinutes);

    return {
      time,
      capacity,
      taken,
      isPast,
      available: !isPast && taken < capacity,
    };
  });
}

/**
 * The soonest bookable slot, searching forward.
 *
 * This is the figure the catalogue card leads with. A price with no date is
 * only half an offer: "متاح غداً 9:00" is what decides between two hospitals
 * charging the same.
 *
 * Bounded at `horizonDays` so a service that is configured but never open
 * terminates instead of walking the calendar forever.
 */
export function nextAvailableSlot({
  service,
  bookings = [],
  fromDate,
  horizonDays = 60,
  now = new Date(),
}) {
  if (!service) return null;

  const start = fromDate
    ? new Date(`${fromDate}T00:00:00`)
    : new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (Number.isNaN(start.getTime())) return null;

  for (let offset = 0; offset < horizonDays; offset += 1) {
    const day = new Date(start.getFullYear(), start.getMonth(), start.getDate() + offset);
    const dateKey = `${day.getFullYear()}-${String(day.getMonth() + 1).padStart(2, '0')}-${String(
      day.getDate(),
    ).padStart(2, '0')}`;

    const free = serviceAvailability({ service, bookings, dateKey, now }).find((s) => s.available);
    if (free) return { date: dateKey, time: free.time, daysAway: offset };
  }

  return null;
}

/* ==========================================================================
   Home visits
   ========================================================================== */

/**
 * Whether a home visit reaches this far.
 *
 * A range of zero means "no stated limit" rather than "nowhere" — a centre that
 * enables home visits and leaves the range blank means it travels, and reading
 * that as a refusal would silently hide the service it just turned on.
 */
export function homeVisitReaches(service, distanceKm) {
  if (!service?.homeVisitAvailable) return false;
  const range = num(service.homeVisitRangeKm, 0);
  if (range <= 0) return true;
  const distance = num(distanceKm, NaN);
  if (!Number.isFinite(distance)) return true;
  return distance <= range;
}

/* ==========================================================================
   Quotas
   ========================================================================== */

/**
 * How much of its sub-account allowance an institution has used.
 *
 * The admin sets the ceiling; the institution manages itself underneath it.
 * A ceiling of zero is a real limit — an institution that may create nobody —
 * so it is not treated as "unlimited".
 */
export function quotaState({ used = 0, limit = 0 } = {}) {
  const cap = Math.max(0, Math.round(num(limit, 0)));
  const count = Math.max(0, Math.round(num(used, 0)));
  const remaining = Math.max(0, cap - count);

  return {
    used: count,
    limit: cap,
    remaining,
    full: count >= cap,
    // Over the line rather than merely at it: the admin lowered a ceiling under
    // accounts that already exist. Worth showing differently from "full".
    exceeded: count > cap,
    percent: cap > 0 ? Math.min(100, Math.round((count / cap) * 100)) : 100,
  };
}

/* ==========================================================================
   Presentation helpers
   ========================================================================== */

/** Group a flat service list by category, in the catalogue's display order. */
export function groupByCategory(services = []) {
  const order = Object.values(SERVICE_CATEGORIES);
  const buckets = new Map();

  for (const service of Array.isArray(services) ? services : []) {
    if (!service) continue;
    const key = service.category;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(service);
  }

  return [...buckets.entries()]
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([category, items]) => ({ category, label: SERVICE_CATEGORY_LABELS[category] || category, items }));
}

/** "٣ أيام" style summary of how far off the soonest slot is. */
export function describeAvailability(next) {
  if (!next) return 'لا توجد مواعيد متاحة';
  if (next.daysAway === 0) return 'متاح اليوم';
  if (next.daysAway === 1) return 'متاح غداً';
  if (next.daysAway <= 7) return `متاح خلال ${next.daysAway} أيام`;
  return `أقرب موعد بعد ${next.daysAway} يوماً`;
}

export default {
  INSTITUTION_TYPES,
  SERVICE_CATEGORIES,
  PATIENT_TABS,
  capabilitiesFor,
  canOffer,
  priceService,
  cheapestOffer,
  serviceSlotsForDate,
  serviceAvailability,
  nextAvailableSlot,
  homeVisitReaches,
  quotaState,
  groupByCategory,
};
